/**
 * Basecamp Dashboard - routes section views and project home.
 */

import { useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import {
  useBasecampProjects,
  useBasecampTodos,
  useBasecampProjectActivity,
  useBasecampAssignments,
  useBasecampProjectStats,
  useBasecampTodosWithWorkSpecs,
  useBasecampTodoLists,
} from '@/hooks/use-basecamp';
import {
  buildBasecampProjectHomeTrail,
  dispatchIntegrationTabEnrich,
} from '@/lib/basecamp-breadcrumbs';
import { dueDateStatus } from '@/lib/utils';
import type { IntegrationMainViewProps } from '@/types/integration';
import { BasecampSectionView } from './basecamp-section-view';
import { BasecampActivityFeed } from './basecamp-activity-feed';
import { BasecampProjectHome } from './basecamp-project-home';

interface BasecampDashboardProps extends IntegrationMainViewProps {
  onAction?: (action: string, data?: unknown) => void;
}

export function BasecampDashboard({
  initialData,
  onAction: _onAction,
}: BasecampDashboardProps) {
  const organizationId = initialData?.organizationId as string | undefined;
  const section = initialData?.section as string | undefined;

  if (section === 'activity') {
    return <BasecampActivityFeed organizationId={organizationId} />;
  }

  if (
    section === 'bookmarks' ||
    section === 'mystuff' ||
    section === 'todos' ||
    section === 'projects'
  ) {
    return (
      <BasecampSectionView section={section} organizationId={organizationId} />
    );
  }

  return (
    <BasecampProjectDashboard
      initialData={initialData}
      organizationId={organizationId}
    />
  );
}

function BasecampProjectDashboard({
  initialData,
  organizationId,
}: {
  initialData?: IntegrationMainViewProps['initialData'];
  organizationId?: string;
}) {
  const {
    projects,
    isLoading: projectsLoading,
    error: projectsError,
    refetch: refetchProjects,
  } = useBasecampProjects(organizationId);

  const validProjects = (projects || []).filter(
    (p): p is NonNullable<typeof p> => p != null && p.id != null,
  );

  const projectId = initialData?.projectId || validProjects[0]?.id;
  const projectIdStr = projectId?.toString();
  const selectedProject =
    validProjects.find((p) => p.id?.toString() === projectIdStr) ||
    validProjects[0];

  const { todos, isLoading: todosLoading, refetch: refetchTodos } =
    useBasecampTodos(projectId);
  const {
    activities,
    isLoading: activitiesLoading,
    refetch: refetchActivities,
  } = useBasecampProjectActivity(projectId, 7);
  const {
    assignments,
    isLoading: assignmentsLoading,
    cachedAt: assignmentsCachedAt,
    refetch: refetchAssignments,
  } = useBasecampAssignments(false, organizationId);
  const { todos: todosWithWorkSpecs } = useBasecampTodosWithWorkSpecs(
    projectIdStr || null,
    organizationId,
  );
  const {
    todoLists,
    isLoading: todoListsLoading,
    error: todoListsError,
    apiMessage: todoListsMessage,
    refetch: refetchTodoLists,
  } = useBasecampTodoLists(projectId);

  const workSpecMap = useMemo(() => {
    const map = new Map<
      string,
      { hasWorkSpec: boolean; workSpecPath: string | null }
    >();
    todosWithWorkSpecs.forEach((todo) => {
      map.set(todo.todoId, {
        hasWorkSpec: todo.hasWorkSpec,
        workSpecPath: todo.workSpecPath,
      });
    });
    return map;
  }, [todosWithWorkSpecs]);

  const handleSyncAll = async () => {
    await Promise.all([
      refetchProjects(),
      refetchTodos(true),
      refetchActivities(true),
      refetchAssignments(true, undefined, projectId ?? undefined),
      refetchTodoLists(true),
    ]);
  };

  const { stats: projectStats } = useBasecampProjectStats(
    projectIdStr || null,
    organizationId,
  );

  const metrics = useMemo(() => {
    if (!todos) return null;

    const overdueTodos = todos.filter((todo) => {
      if (!todo.due_on || todo.completed) return false;
      return dueDateStatus(todo.due_on).isOverdue;
    });

    const upcomingTodos = todos.filter((todo) => {
      if (!todo.due_on || todo.completed) return false;
      return dueDateStatus(todo.due_on).isDueSoon;
    });

    const activeTodos = todos.filter((todo) => !todo.completed);

    return {
      overdue: overdueTodos.length,
      upcoming: upcomingTodos.length,
      active: activeTodos.length,
      activity: activities?.length || 0,
      workSpecs: projectStats?.workSpecs || 0,
    };
  }, [todos, activities, projectStats]);

  const myTodos = useMemo(() => {
    if (!assignments || !Array.isArray(assignments)) return [];
    return assignments
      .filter(
        (a) =>
          a?.project?.id?.toString() === projectId?.toString() && !a.completed,
      )
      .slice(0, 10);
  }, [assignments, projectId]);

  // Must run before any early return (Rules of Hooks).
  useEffect(() => {
    if (!projectIdStr || !selectedProject?.name) return;
    try {
      localStorage.setItem(
        `basecamp-project-name:${projectIdStr}`,
        selectedProject.name,
      );
    } catch {
      /* ignore quota */
    }
    dispatchIntegrationTabEnrich({
      tabId: `basecamp-project-${projectIdStr}`,
      breadcrumbTrail: buildBasecampProjectHomeTrail(
        projectIdStr,
        selectedProject.name,
      ),
      title: selectedProject.name,
      projectName: selectedProject.name,
    });
  }, [projectIdStr, selectedProject?.name]);

  if (projectsLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading projects...</p>
        </div>
      </div>
    );
  }

  if (projectsError) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
          <h2 className="mb-2 text-lg font-semibold">Failed to Load Projects</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            {projectsError.message || 'An error occurred'}
          </p>
          <Button onClick={() => refetchProjects()} variant="outline" size="sm">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!selectedProject) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="mb-2 text-lg font-semibold">No Projects Found</h2>
          <p className="text-muted-foreground">
            No Basecamp projects available. Check your Basecamp account.
          </p>
        </div>
      </div>
    );
  }

  const syncing =
    todosLoading ||
    activitiesLoading ||
    projectsLoading ||
    assignmentsLoading ||
    todoListsLoading;

  return (
    <BasecampProjectHome
      project={selectedProject}
      organizationId={organizationId}
      todoLists={todoLists}
      todoListsLoading={todoListsLoading}
      todoListsError={todoListsError}
      todoListsMessage={todoListsMessage}
      activities={activities}
      activitiesLoading={activitiesLoading}
      myTodos={myTodos}
      assignmentsLoading={assignmentsLoading}
      metrics={metrics}
      assignmentsCachedAt={assignmentsCachedAt}
      syncing={syncing}
      onSyncAll={handleSyncAll}
      onRetryTodoLists={() => refetchTodoLists(true)}
      workSpecMap={workSpecMap}
    />
  );
}
